"use strict";
// packages/rdti-schema/src/registry.ts
//
// Maps an Australian income year string (e.g. "2024-25") to the
// RDTISchemaVersion that governs project + activity field definitions.
// `schemaVersion` ("FY24", "FY25") is stored on every project row; lookups
// here drive UI rendering, compliance scoring, exports, and AI prompts.
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveSchemaVersionForTaxYear = resolveSchemaVersionForTaxYear;
exports.getSchemaVersion = getSchemaVersion;
exports.listSchemaVersions = listSchemaVersions;
exports.getFieldsForSection = getFieldsForSection;
exports.findField = findField;
const fy24_1 = require("./versions/fy24");
const fy25_1 = require("./versions/fy25");
const VERSIONS = {
    FY24: fy24_1.FY24,
    FY25: fy25_1.FY25,
};
/**
 * Convert a taxYear string ("2025-26") to the ISO date of that income year's
 * start (1 July of the first year — "2025-07-01"). Returns null for malformed
 * input.
 */
function incomeYearStartDate(taxYear) {
    const startYear = Number.parseInt(taxYear.slice(0, 4), 10);
    if (!Number.isFinite(startYear))
        return null;
    return `${startYear}-07-01`;
}
/**
 * Resolve the schema version that applies to a given taxYear. A schema is
 * the active one for every income year starting on or after its
 * `effectiveFrom`, until a newer schema supersedes it.
 *
 * Example with FY24 (2023-07-01) and FY25 (2024-07-01) registered:
 *   "2023-24" → FY24
 *   "2024-25" → FY25
 *   "2025-26" → FY25  (still FY25 — no newer schema exists)
 *   "2030-31" → FY25  (still FY25 until we register FY26+)
 *
 * Add a new RDTISchemaVersion to VERSIONS to introduce a new ruleset; no
 * registry edits needed.
 */
function resolveSchemaVersionForTaxYear(taxYear) {
    var _a, _b;
    const startDate = incomeYearStartDate(taxYear);
    if (!startDate)
        return 'FY25'; // unparseable — assume latest
    // Sort schemas by effectiveFrom ascending; pick the latest one whose
    // effectiveFrom is on or before the income-year start.
    const sorted = Object.values(VERSIONS).sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
    let active = (_b = (_a = sorted[0]) === null || _a === void 0 ? void 0 : _a.version) !== null && _b !== void 0 ? _b : 'FY25';
    for (const schema of sorted) {
        if (schema.effectiveFrom <= startDate)
            active = schema.version;
        else
            break;
    }
    return active;
}
/** Look up a schema by version ID. Throws if unknown — version strings are stored in DB and must round-trip. */
function getSchemaVersion(version) {
    const schema = VERSIONS[version];
    if (!schema) {
        throw new Error(`Unknown RDTI schema version: ${version}`);
    }
    return schema;
}
/** All registered versions, newest first. */
function listSchemaVersions() {
    return Object.values(VERSIONS).sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
}
function getFieldsForSection(version, section) {
    return getSchemaVersion(version)[section];
}
function findField(version, section, key) {
    return getFieldsForSection(version, section).find((f) => f.key === key);
}
